import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, Plus } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { cn } from '../../lib/cn';
import { Breadcrumbs } from './Breadcrumbs';
import { SearchBox } from './SearchBox';
import { ThemeToggle } from './ThemeToggle';

/**
 * ConsoleShell utility bar, desktop only. 56px, sticky, hairline bottom.
 *
 * It carries four things and refuses a fifth: where you are (breadcrumb),
 * search, notifications, and the one primary create action. Everything else
 * belongs on the page.
 *
 * The bell takes `.dsk-dot-badge`, not a count. A dot says there is something
 * new without pretending to know how many, which is the honest version when the
 * app has no notification count to give.
 *
 * The primary action is INK (`.dsk-btn--primary`). Ink is command. It is never
 * accent and never a gradient.
 *
 * @param {{
 *   breadcrumbs?: Array<{ label: string, to?: string }>,
 *   search?: { value?: string, onChange?: Function, onSubmit?: Function, placeholder?: string },
 *   showSearch?: boolean,
 *   notifications?: { hasUnread?: boolean, to?: string, onClick?: () => void },
 *   primaryAction?: { label: string, to?: string, onClick?: () => void, icon?: object },
 *   children?: React.ReactNode,
 *   className?: string
 * }} props
 */
export function UtilityBar({
  breadcrumbs = [],
  search,
  showSearch = true,
  notifications,
  primaryAction,
  children,
  className,
}) {
  const ActionIcon = primaryAction?.icon || Plus;

  return (
    <header className={cn('dsk-utilitybar', className)}>
      <Breadcrumbs items={breadcrumbs} />

      {showSearch && (
        <SearchBox
          className="dsk-utilitybar__search"
          value={search?.value}
          onChange={search?.onChange}
          onSubmit={search?.onSubmit}
          placeholder={search?.placeholder || 'Search your listings and orders'}
          label="Search the console"
        />
      )}

      <div className="dsk-utilitybar__end">
        {children}

        <ThemeToggle track="desktop" />

        {notifications &&
          (notifications.to ? (
            <Link to={notifications.to} className="dsk-iconbtn" aria-label="Notifications">
              <Icon as={Bell} />
              {notifications.hasUnread && <span className="dsk-dot-badge" />}
            </Link>
          ) : (
            <button
              type="button"
              className="dsk-iconbtn"
              onClick={notifications.onClick}
              aria-label="Notifications"
            >
              <Icon as={Bell} />
              {notifications.hasUnread && <span className="dsk-dot-badge" />}
            </button>
          ))}

        {primaryAction &&
          (primaryAction.to ? (
            <Link to={primaryAction.to} className="dsk-btn dsk-btn--primary dsk-btn--sm">
              <Icon as={ActionIcon} size="sm" />
              {primaryAction.label}
            </Link>
          ) : (
            <button
              type="button"
              className="dsk-btn dsk-btn--primary dsk-btn--sm"
              onClick={primaryAction.onClick}
            >
              <Icon as={ActionIcon} size="sm" />
              {primaryAction.label}
            </button>
          ))}
      </div>
    </header>
  );
}

export default UtilityBar;
